import React from "react";

import { Alert } from "@hdir/ui/alert";
import { Link } from "@hdir/ui/link";
import { Spinner } from "@hdir/ui/spinner";
import { FontAwesomeIconWrapper } from "@dip/helsecore/src/components/shared/Wrappers/FontAwesomeIconWrapper";
import HighchartsReact from "highcharts-react-official";
import { faFaceMeh, faFaceSmile } from "@fortawesome/pro-light-svg-icons";
import Highcharts from "highcharts";
import { HighchartsDatatype, HighchartsViewProps } from "../../../site/parts/highcharts/types.d";
// Initialize Highcharts modules
import "highcharts/modules/exporting";
import "highcharts/modules/export-data";
import "highcharts/modules/accessibility";

type HighchartsListProps = {
  categories?: HighchartsViewProps["categories"]
  isLoading: boolean
  data?: HighchartsDatatype
  compareData?: HighchartsDatatype
  municipalityName: string
  compareMunicipalityName?: string
}

function HighchartsList({
  compareMunicipalityName, municipalityName, isLoading, categories, data, compareData
}: HighchartsListProps) {
  return (
    categories && categories.map(({
      iconAlt, icon, title, preface, statistics
    }) => (
      <>
        <div className="flex flex-row gap-lg items-center mb-xxl">
          {icon && <img className="w-16" alt={iconAlt} src={icon} />}
          <div className="flex flex-col gap-md">
            <h2 className="text-xxl font-bold leading-7">{title}</h2>
            <p className="text-md leading-6">{preface}</p>
          </div>
        </div>
        {statistics.map((item) => {
          let content;

          if (isLoading) {
            content = (
              <div className="h-[92px] flex justify-center items-center">
                <Spinner />
              </div>
            );
          } else if (item.id === "501" || item.id === "502" || item.id === "503") {
            content = (
              <div className="flex flex-col gap-md">
                <div className="flex flex-row gap-xl">
                  <div className="flex flex-row items-center">
                    <span className="text-md font-bold">{`${municipalityName}:`}</span>
                    <FontAwesomeIconWrapper
                      size="2xl"
                      className="px-xs"
                      color={(data && data[item.id]?.ja_nei.kommune.kommuneverdi === "Ja") ? "#025169" : "#A0002E"}
                      icon={data && data[item.id]?.ja_nei.kommune.kommuneverdi === "Ja" ? faFaceSmile : faFaceMeh}
                    />
                    <span className="text-md font-bold">{data ? data[item.id]?.ja_nei.kommune.kommuneverdi : "Mangler data"}</span>
                  </div>
                  {compareData?.[item.id]
                    && (
                      <div className="flex flex-row items-center">
                        <span className="text-md font-bold">{`${compareData[item.id]?.ja_nei.kommune.kommunenavn}:`}</span>
                        <FontAwesomeIconWrapper
                          size="2xl"
                          className="px-xs"
                          color={(compareData && compareData[item.id]?.ja_nei.kommune.kommuneverdi === "Ja") ? "#025169" : "#A0002E"}
                          icon={compareData && compareData[item.id]?.ja_nei.kommune.kommuneverdi === "Ja" ? faFaceSmile : faFaceMeh}
                        />
                        <span className="text-md font-bold">{compareData ? compareData[item.id]?.ja_nei.kommune.kommuneverdi : "Mangler data"}</span>
                      </div>
                    )}
                </div>
                <div className="text-md mt-md">{data?.[item.id]?.ja_nei.beskrivelse}</div>
                <div className="flex flex-row gap-lg">
                  <span className="text-md font-bold">{`${data?.[item.id]?.ja_nei.fylke.fylkenavn}: ${data?.[item.id]?.ja_nei.fylke.fylkeverdi}`}</span>
                  <span className="text-md font-bold">{`${data?.[item.id]?.ja_nei.norge.norgenavn}: ${data?.[item.id]?.ja_nei.norge.norgeverdi}`}</span>
                  {compareData?.[item.id] && <span className="text-md font-bold">{`${compareData?.[item.id]?.ja_nei.fylke.fylkenavn}: ${compareData?.[item.id]?.ja_nei.fylke.fylkeverdi}`}</span>}
                </div>
                {item.link && (
                  <Link variant="button-secondary" size="medium" href={item.link}>
                    {item.linkTitle}
                  </Link>
                )}
              </div>
            );
          } else {
            content = (
              <>
                {data?.[item.id] ? (
                  <HighchartsReact
                    key={`main-${item.id}`}
                    highcharts={Highcharts}
                    options={data[item.id] as Highcharts.Options}
                    className="max-h-52"
                  />
                ) : (
                  <Alert variant="warning" fullWidth className="h-[52px] items-center" title={`${municipalityName ? `Mangler data for ${municipalityName}` : "Mangler data"}`} />
                )}
                {compareData && (
                  compareData[item.id] ? (
                    <HighchartsReact
                      key={`compare-${item.id}`}
                      highcharts={Highcharts}
                      options={compareData[item.id] as Highcharts.Options}
                    />
                  ) : (
                    compareMunicipalityName && <Alert variant="warning" fullWidth className="h-[52px] items-center" title={`Mangler data for ${compareMunicipalityName}`} />
                  )
                )}
                {item.link && (
                  <Link variant="button-secondary" size="medium" href={item.link}>
                    {item.linkTitle}
                  </Link>
                )}
              </>
            );
          }
          return (
            <div className="flex flex-col gap-md mb-xxl" key={item.title}>
              <h3 className="text-xl">{item.title}</h3>
              <p className="text-md leading-6">{item.preface}</p>
              {content}
            </div>
          );
        })}
      </>
    ))
  );
}

export default function(props: Readonly<HighchartsListProps>) {
  return <HighchartsList {...props} />;
}
